import { autoElement } from '../../../webmodule-common/components/src/webmodule-components';
import { currentUserClaims } from '../../../webmodule-common/other/currentuser-claims';
import { getCredentials } from '../../api/licenseserver-credentials';
import { html } from 'lit';
import {
  BaseLoginModalEditProperties,
  ILicenseServerUIStaffPageProvider,
  ILoginClaimManager,
  IStaffPage,
  licenseServerUIBootLoader,
  ViewBaseLogin
} from '../data/licenseserver-integration';
import { PageControlTabWithIndependantSaving } from '../../../webmodule-common/other/ui/data-entry-screen-base';
import { PromiseTemplate, Snippet } from '../../../webmodule-common/interop/webmodule-interop';
import { tlang } from '../../../webmodule-common/other/language/lang';
import { claimIdentity, userSecurity } from '../../../webmodule-common/other/api/user-security';
import { emptyGuid } from '../../../webmodule-common/other/api/guid';
import { repeat } from 'lit/directives/repeat.js';
import { getTerritoryManager } from '../data/territory-manager';

@autoElement()
export class SettingsStaffView extends PageControlTabWithIndependantSaving {
  private staffPageProvider: ILicenseServerUIStaffPageProvider;
  private userConsole?: IStaffPage;
  private territoryManager = getTerritoryManager();

  constructor() {
    super();
    this.pageFragment = 'staff';

    this.staffPageProvider = this.staffPageProviderFactory();

    console.log(JSON.stringify(this.staffPageProvider.getColumnProperties?.()));
  }

  public async afterConstruction(): Promise<void> {
    const licUI = await licenseServerUIBootLoader();
    await this.territoryManager.needsTerritories();
    this.userConsole = licUI?.getStaffPage(this.staffPageProvider);
  }

  public allowDeletePage(): boolean {
    return false;
  }

  async prepareForSave(): Promise<void> {
    //
  }

  public internalDataChanged(): boolean {
    return false;
  }

  async onEnter(): Promise<void> {
    this.requestUpdate();
  }

  public buttonMenu(): Snippet {
    const isAdmin = currentUserClaims().isAdmin;
    const buttons = this.userConsole?.actions().map(x => {
      return html` <button class="btn btn-primary" ?disabled=${!x.enabled || !isAdmin} @click=${x.event}>
        ${x.label}
      </button>`;
    });
    return html`${buttons}`;
  }

  protected getCaption(): Snippet {
    return tlang`Staff`;
  }

  protected async internalSaveData(): Promise<boolean> {
    //nothing to do
    return true;
  }
  get isAdmin(): boolean {
    return userSecurity().claimIsTrue(claimIdentity.admin);
  }

  protected async bodyTemplate(): PromiseTemplate {
    return this.userConsole?.template() ?? html``;
  }

  buildModalProperties(): BaseLoginModalEditProperties[] {
    const results: BaseLoginModalEditProperties[] = [];
    if (this.isAdmin) results.push(this.buildPowerUserProperty());
    if (this.isAdmin) results.push(this.buildDealerTerritoryProperty());

    return results;
  }
  buildDealerTerritoryProperty(): BaseLoginModalEditProperties {
    let dealerTerritoryId: string | undefined;
    const changeEvent = (e: Event) => (dealerTerritoryId = (e.currentTarget as HTMLSelectElement).value);
    const allTerritories = this.territoryManager.territories;
    const frameConfigEditor: BaseLoginModalEditProperties = {
      inputId: 'dealerTerritory',
      visible: this.isAdmin,
      editControl: (claimManager: ILoginClaimManager) => {
        if (!dealerTerritoryId)
          dealerTerritoryId = claimManager.getClaim(claimIdentity.supplierDealerTerritory) ?? 'false';
        return html`<select @change=${changeEvent} id="dealerTerritory" class="form-select">
          <option ?selected=${dealerTerritoryId === 'false'} value=${emptyGuid}>
            ${tlang`All %%franchisee%% Territories`}
          </option>
          ${repeat(
            allTerritories,
            item => item.id,
            item => html`<option ?selected=${dealerTerritoryId === item.id} value=${item.id}>${item.name}</option>`
          )}
        </select>`;
      },
      label: tlang`%%Franchisee%% Territory`,
      propertyId: 1,
      save: (claimManager: ILoginClaimManager) => {
        claimManager.setClaim(claimIdentity.supplierDealerTerritory, dealerTerritoryId ?? emptyGuid);
      }
    };
    return frameConfigEditor;
  }
  buildPowerUserProperty(): BaseLoginModalEditProperties {
    let powerUserValue: string | undefined;
    const changeEvent = (e: Event) => (powerUserValue = (e.currentTarget as HTMLSelectElement).value);

    const frameConfigEditor: BaseLoginModalEditProperties = {
      inputId: 'powerUser',
      visible: this.isAdmin,
      editControl: (claimManager: ILoginClaimManager) => {
        if (!powerUserValue) powerUserValue = claimManager.getClaim(claimIdentity.frameConfigPowerUser) ?? 'false';
        return html`<select @change=${changeEvent} id="powerUser" class="form-select">
          <option ?selected=${powerUserValue === 'false'} value=${'false'}>
            ${tlang`%%franchisee%% Standard %%frame%% Configurator`}
          </option>
          <option ?selected=${powerUserValue === 'true'} value=${'true'}>
            ${tlang`%%franchisee%% Power User %%frame%% Configurator`}
          </option>
        </select>`;
      },
      label: tlang`%%frame%% Power User`,
      propertyId: 1,
      save: (claimManager: ILoginClaimManager) => {
        claimManager.setClaim(claimIdentity.frameConfigPowerUser, powerUserValue ?? 'false');
      }
    };
    return frameConfigEditor;
  }

  protected staffPageProviderFactory(): ILicenseServerUIStaffPageProvider {
    return {
      credentials: getCredentials(),
      onAfterSave: async (login: ViewBaseLogin) => {
        console.log(`login saved ${login.baseLogin.userName}`);
      },
      onBeforeSave: async (login: ViewBaseLogin) => {
        console.log(`login saving ${login.baseLogin.userName}`);
        return true;
      },
      canEditUser: (_viewLogin: ViewBaseLogin): boolean => {
        //full admin can edit anyone
        const claims = currentUserClaims();
        if (claims.isAdmin) return true;
        //if admin, and only admin, we can see everything.
        return false;
      },
      getColumnProperties: () => {
        console.log('get column properties');
        return [
          {
            displayValue: (login: ViewBaseLogin) => {
              const allStr =
                login.deploymentClaims[claimIdentity.frameConfigPowerUser] === 'true' ? tlang`Yes` : tlang`No`;
              return html`<span>${allStr}</span>`;
            },
            title: () => tlang`Power User`
          }
        ];
      },
      getModalProperties: () => this.buildModalProperties(),
      uiChanged: () => this.requestUpdate()
    };
  }
}
